function SettingTiktokApi({
  lang,
  confirmDialog,
  business_data,
  toast,
  chatCenter,
  setChatCenter,
}) {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isGetLoading, setIsGetLoading] = React.useState(false);
  const [tiktokError, setTiktokError] = React.useState(false);
  const [getTiktokAccountsList, setGetTiktokAccountsList] = React.useState([]);
  const [showWarningConnectModal, setShowWarningConnectModal] =
    React.useState(false);
  const [tiktokAccountsConnecting, setTiktokAccountsConnecting] =
    React.useState([]);
  const [maxPage, setMaxPage] = React.useState(0);
  const tiktokPopupRef = React.useRef(null);
  const tiktokStateRef = React.useRef("");
  const pendingTikTokCodeRef = React.useRef("");
  const readPath = rChatReadPath;

  const getTiktokAccountId = (account = {}) => rChatGetTikTokAccountId(account);

  const normalizeTiktokAccount = (account = {}) => {
    const accountId = getTiktokAccountId(account);
    const normalizedAccount = rChatNormalizeTikTokConnection(account, {
      defaultConnectionStatus:
        account.connection_status !== undefined
          ? rChatNormalizeBooleanFlag(account.connection_status)
          : true,
    });

    return Object.assign({}, normalizedAccount, {
      account_id: accountId || normalizedAccount.account_id || "",
      tiktok_account_id:
        normalizedAccount.tiktok_account_id || accountId || "",
      warning_data: normalizedAccount.warning_data || account,
    });
  };

  const getAccountStatus = (account = {}) => rChatGetTikTokAccountStatus(account);

  const getWarningText = (account = {}) => {
    const currentLanguage =
      typeof moment !== "undefined" && typeof moment.locale === "function"
        ? moment.locale() || "en"
        : "en";
    return rChatGetTikTokWarningMetaText(account, lang || {}, currentLanguage);
  };

  const getConnectedMetaText = (account = {}) => {
    const connectedAt = readPath(account, ["created_time"], "")
    const connectedBy = readPath(account, ["connected_by"], "")

    const formattedDate = connectedAt
      ? typeof moment !== "undefined" && typeof moment === "function"
        ? moment(connectedAt).format("D MMM YYYY HH:mm")
        : connectedAt
      : "";

    if (formattedDate && connectedBy) {
      return `${lang.__connected_on} ${formattedDate} ${lang.__connected_by} ${connectedBy}`;
    }

    if (formattedDate) {
      return `${lang.__connected_on} ${formattedDate}`;
    }

    if (connectedBy) {
      return `${lang.__connected_by} ${connectedBy}`;
    }

    return "";
  };
  
  const getTikTokConnectErrorMessage = (payload = {}) => {
    const errorCode = payload?.code

    if (errorCode === "TIKTOK_NOT_BUSINESS_ACCOUNT") {
      return (
        lang.__tiktok_not_business_account ||
        "This TikTok account is not a Business Account."
      );
    }

    return lang.__tiktok_connect_fail || "Failed to connect TikTok account.";
  };

  const normalizeWarningAccounts = (warningPayload) => {
    const warningItems = Array.isArray(warningPayload)
      ? warningPayload
      : warningPayload
        ? [warningPayload]
        : [];

    return warningItems
      .filter(Boolean)
      .map((item) => {
        const normalized = normalizeTiktokAccount(item);
        const businessDetail = Object.assign(
          {},
          readPath(item, ["businessDetail"], {}),
          readPath(normalized, ["businessDetail"], {})
        );

        return Object.assign({}, normalized, {
          name: normalized.title || item.title || item.name || "",
          businessDetail,
        });
      });
  };

  const getTikTokOAuthConfig = () => {
    const mainConfig = typeof main !== "undefined" ? main : {};

    return {
      authUrl:
        mainConfig.tiktok_oauth_url ||
        "https://www.tiktok.com/v2/auth/authorize",
      clientKey:
        mainConfig.tiktok_client_key ||
        readPath(mainConfig, ["tiktok", "appId"], "") ||
        "",
      redirectUri:
        mainConfig.tiktok_redirect_uri ||
        `${window.location.origin}/tiktok-callback`,
      scope: mainConfig.tiktok_oauth_scope || "",
    };
  };

  const getCenteredPopupFeatures = (width = 800, height = 600) => {
    const dualScreenLeft =
      window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop =
      window.screenTop !== undefined ? window.screenTop : window.screenY;

    const currentWindowWidth =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      screen.width;
    const currentWindowHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      screen.height;

    const left = Math.max(
      0,
      Math.round(dualScreenLeft + (currentWindowWidth - width) / 2)
    );
    const top = Math.max(
      0,
      Math.round(dualScreenTop + (currentWindowHeight - height) / 2)
    );

    return `width=${width},height=${height},left=${left},top=${top}`;
  };

  const getTikTokStateStorageKey = () =>
    `tiktok_oauth_state:${business_data.uid}:${business_data.app_id}`;

  const clearTikTokOAuthState = () => {
    tiktokStateRef.current = "";
    tiktokPopupRef.current = null;
    sessionStorage.removeItem(getTikTokStateStorageKey());
  };

  const generateTikTokOAuthState = () => {
    if (window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  };

  // This useEffect handles the initial data fetching and the message listener for the TikTok callback.
  React.useEffect(() => {
    getSettingAccount();

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin || !event.data) {
        return;
      }

      if (tiktokPopupRef.current && event.source !== tiktokPopupRef.current) {
        return;
      }

      if (event.data.type === "tiktok-error") {
        clearTikTokOAuthState();
        toast.error(lang.__tiktok_connect_fail || "Failed to connect TikTok account.");
        return;
      }

      if (
        event.data.type === "tiktok-code" &&
        event.data.code &&
        event.data.state &&
        event.data.state === tiktokStateRef.current &&
        event.data.state === sessionStorage.getItem(getTikTokStateStorageKey())
      ) {
        clearTikTokOAuthState();
        connectTiktokAccount(event.data.code);
      }
    };

    window.addEventListener("message", handleMessage);

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  const getSettingAccount = () => {
    setIsGetLoading(true);
    return new Promise((resolve, reject) => {
      axios({
        method: "get",
        url: `${backend}tiktok/${business_data.uid}/${business_data.app_id}/accounts`,
        headers: {
          "Content-type": "application/json;charset=UTF-8",
          "X-VC-Access-Token": business_data.access,
          Authorization: `Bearer ${business_data.token}`,
        },
      })
        .then((res) => {
          if (res.data.result === 0) {
            const rawAccountList = readPath(res, ["data", "data"], []);
            const tiktokData = Array.isArray(rawAccountList)
              ? rawAccountList.map(normalizeTiktokAccount)
              : [];
            const maxPageFromApi = Number(
              readPath(res, ["data", "max_page"], tiktokData.length)
            );

            settingChatCenter(tiktokData);
            setGetTiktokAccountsList(tiktokData);
            setMaxPage(
              Number.isFinite(maxPageFromApi) && maxPageFromApi > 0
                ? maxPageFromApi
                : tiktokData.length
            );
            setTiktokError(false);
          } else {
            setTiktokError(true);
            reject(res.data);
          }
          setIsGetLoading(false);
          setIsLoading(false);
          resolve();
        })
        .catch((error) => {
          console.error("Error fetching TikTok accounts:", error);
          setTiktokError(true);
          setIsGetLoading(false);
          setIsLoading(false);
          reject(error);
        });
    });
  };

  const connectTiktokAccount = (code, skipCheck = false) => {
    pendingTikTokCodeRef.current = code;

    return axios({
      method: "POST",
      url: `${backend}tiktok/${business_data.uid}/${business_data.app_id}/accounts/connect`,
      data: { code: code, skipCheck: !!skipCheck },
      headers: {
        "Content-type": "application/json;charset=UTF-8",
        "X-VC-Access-Token": business_data.access,
        Authorization: `Bearer ${business_data.token}`,
      },
    })
      .then((res) => {
        if (res.data.result === 0) {
          pendingTikTokCodeRef.current = "";
          setShowWarningConnectModal(false);
          setTiktokAccountsConnecting([]);
          toast.success(
            lang.__tiktok_connect_success ||
              "TikTok account connected successfully!"
          );
          getSettingAccount(); // Refresh the list of accounts
        } else {
          const resultCode = Number(readPath(res, ["data", "result"], 1));
          if ((resultCode === 2 || resultCode === 3) && !skipCheck) {
            const warningAccounts = normalizeWarningAccounts(
              readPath(res, ["data", "data"], [])
            );

            if (warningAccounts.length) {
              setTiktokAccountsConnecting(warningAccounts);
              setShowWarningConnectModal(true);
              return;
            }
          }

          pendingTikTokCodeRef.current = "";
          setShowWarningConnectModal(false);
          setTiktokAccountsConnecting([]);
          toast.error(getTikTokConnectErrorMessage(res.data));
        }
      })
      .catch((error) => {
        console.error("Error connecting TikTok account:", error.response?.data);
        pendingTikTokCodeRef.current = "";
        setShowWarningConnectModal(false);
        setTiktokAccountsConnecting([]);
        toast.error(getTikTokConnectErrorMessage(error.response?.data || error));
      });
  };

  const saveTiktokWithSkipCheck = () => {
    if (!pendingTikTokCodeRef.current) {
      toast.error(lang.__tiktok_connect_fail || "Failed to connect TikTok account.");
      return Promise.reject(new Error("missing pending TikTok OAuth code"));
    }

    return connectTiktokAccount(pendingTikTokCodeRef.current, true);
  };

  const settingChatCenter = (tiktok) => {
    let objectChatCenterTiktok = {};
    if (Array.isArray(tiktok)) {
      tiktok.forEach((item) => {
        const accountId = getTiktokAccountId(item);

        if (!accountId) {
          return;
        }

        objectChatCenterTiktok[accountId] = {
          id: accountId,
          name: item.title,
          type: "tiktok",
          connection_status: rChatNormalizeBooleanFlag(item.connection_status),
          warning: rChatNormalizeBooleanFlag(item.warning),
          warning_state: item.warning_state || "",
          is_expired: rChatNormalizeBooleanFlag(item.is_expired),
          warning_data: item,
        };
      });
    }

    const newChatCenter = Object.keys(chatCenter || {}).reduce((result, key) => {
      if (!chatCenter[key] || chatCenter[key].type === "tiktok") {
        return result;
      }
      result[key] = chatCenter[key];
      return result;
    }, {});
    setChatCenter(Object.assign({}, objectChatCenterTiktok, newChatCenter));
  };

  const initiateTiktokConnect = () => {
    const oauthConfig = getTikTokOAuthConfig();

    if (!oauthConfig.clientKey) {
      toast.error(
        lang.__tiktok_config_missing || "TikTok client key is not configured."
      );
      return;
    }

    const state = generateTikTokOAuthState();
    tiktokStateRef.current = state;
    sessionStorage.setItem(getTikTokStateStorageKey(), state);

    const params = new URLSearchParams({
      client_key: oauthConfig.clientKey,
      scope: oauthConfig.scope,
      response_type: "code",
      redirect_uri: oauthConfig.redirectUri,
      disable_auto_auth: "1",
      state,
    });

    const popupWidth = 800;
    const popupHeight = 600;

    tiktokPopupRef.current = window.open(
      `${oauthConfig.authUrl}?${params.toString()}`,
      "tiktokLogin",
      getCenteredPopupFeatures(popupWidth, popupHeight)
    );

    if (!tiktokPopupRef.current) {
      clearTikTokOAuthState();
      toast.error(
        lang.__popup_blocked || "Popup blocked. Please allow popups and try again."
      );
    }
  };

  const disconnectTiktokAccount = (accountId) => {
    confirmDialog(
      lang.__confirm_disconnect_tiktok_title || 'Confirm Disconnect',
      lang.__confirm_disconnect_tiktok_body || 'Are you sure you want to disconnect this TikTok account?',
      {
        submit_text: lang.__disconnect || 'Disconnect',
        btn_class: '-red'
      }
    ).then(() => { // Use .then() for the callback
      axios({
        method: "DELETE",
        url: `${backend}tiktok/${business_data.uid}/${business_data.app_id}/accounts/${accountId}`,
        headers: {
          "X-VC-Access-Token": business_data.access,
          Authorization: `Bearer ${business_data.token}`,
        },
      })
        .then((res) => {
          if (res.data.result === 0) {
            toast.success(
              lang.__tiktok_disconnect_success ||
                "TikTok account disconnected successfully!"
            );
            getSettingAccount(); // Refresh the list
          } else {
            toast.error(
              lang.__tiktok_disconnect_fail ||
                "Failed to disconnect TikTok account."
            );
          }
        })
        .catch((error) => {
          console.error("Error disconnecting TikTok account:", error);
          toast.error(
            lang.__tiktok_disconnect_fail ||
              "Failed to disconnect TikTok account."
          );
        });
    });
  };

  return (
    <>
      {isLoading ? (
        <div class="sp-placeholder">
          <div class="sp-text-help">
            <i class="icon icon-circle-notch animate-spin"></i>{" "}
            <span>{lang.__loading}</span>
          </div>
        </div>
      ) : tiktokError ? (
        <div class="sp-placeholder">
          <div class="sp-text-help">
            <span>
              {lang.__tiktok_setting_error || "Error loading TikTok settings."}
            </span>
          </div>
        </div>
      ) : (
        <div class="setting-tiktok-api">
          <div class="sp-row setting-tiktok-api__header">
            <div>
              <span class="sp-label">
                {lang.__setting_tiktok_api || "TikTok API Settings"}
              </span>
              <ReactTooltip
                text={lang.__manual_setting_tiktok_api}
                tooltipWidth={160}
              >
                <a
                  href="https://help.readyplanet.com/rchat/chat-center/connect-with-tiktok"
                  target="_blank"
                >
                  <i
                    class="icon-help-circled sp-link"
                    tooltip-placement="right"
                  ></i>
                </a>
              </ReactTooltip>
              <p class="sp-text-help">
                {lang.__setting_tiktok_api_description}
              </p>
            </div>
          </div>
          {isGetLoading ? (
            <div class="sp-placeholder">
              <div class="sp-text-help">
                <i class="icon icon-circle-notch animate-spin"></i>{" "}
                <span>{lang.__loading}</span>
              </div>
            </div>
          ) : (
            <div class="body -space-between setting-tiktok-api__content">
              <div class="setting-tiktok-api__list-section">
                <div class="sp-list-header sp-row -align-middle -space-between setting-tiktok-api__list-header">
                  <span class="sp-label setting-tiktok-api__list-title">
                    <img
                      src="../public/img/TikTok.png"
                      width={20}
                      height={20}
                      class="setting-tiktok-api__channel-icon"
                    />
                    <span>
                      {lang.__connected_tiktok_accounts ||
                        "TikTok Business Accounts"}
                      &nbsp;
                    </span>
                    <span>
                      ({getTiktokAccountsList.length}/{maxPage})
                    </span>
                  </span>
                  {maxPage <= 0 || getTiktokAccountsList.length < maxPage ? (
                    <button
                      class="sp-btn -blue setting-tiktok-api__connect-btn"
                      onClick={initiateTiktokConnect}
                    >
                      {lang.__add_account || "Connect TikTok"}
                    </button>
                  ) : null}
                </div>
                <div class="sp-list -box setting-tiktok-api__list">
                  {!getTiktokAccountsList.length ? (
                    <div class="sp-text-help setting-tiktok-api__empty">
                      {lang.__no_connection}
                    </div>
                  ) : null}
                  {getTiktokAccountsList.map((item, index) => {
                    const accountId = getTiktokAccountId(item);
                    const status = getAccountStatus(item);
                    const hasIssue = status !== "normal";
                    const connectedMetaText = getConnectedMetaText(item);
                    const warningText = getWarningText(item);

                    return (
                      <div
                        class={`item setting-tiktok-api__account setting-tiktok-api__account--${status}`}
                        key={accountId || `tiktok-${index}`}
                      >
                        <div class="sp-row -gap setting-tiktok-api__account-row">
                          <div class="sp-col setting-tiktok-api__profile">
                            {item.profile_image && (
                              <img
                                src={item.profile_image}
                                width={20}
                                height={20}
                                class="setting-tiktok-api__profile-image"
                                alt="Profile"
                              />
                            )}
                            <div class="sp-col -ellipsis setting-tiktok-api__profile-info">
                              <div class="setting-tiktok-api__title">
                                {item.title}
                              </div>
                              {connectedMetaText ? (
                                <span class="setting-tiktok-api__meta">
                                  {connectedMetaText}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="sp-col -wrap setting-tiktok-api__account-actions">
                            {hasIssue ? (
                              <div className="setting-tiktok-api__issue">
                                {warningText ? (
                                  <div
                                    className={`sp-text-help setting-tiktok-api__warning-text setting-tiktok-api__warning-text--${status}${item.profile_image ? " setting-tiktok-api__warning-text--with-avatar" : ""}`}
                                  >
                                    <i className="icon icon-warning-sign"></i>
                                    <span>{warningText}</span>
                                  </div>
                                ) : (
                                  <div
                                    className={`setting-tiktok-api__status-badge setting-tiktok-api__status-badge--${status}`}
                                  >
                                    <i className="icon icon-warning-sign"></i>
                                    <span>
                                      {lang.__connection_have_problem ||
                                        "Connection issue"}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : null}
                            <div className="setting-tiktok-api__buttons">
                              {hasIssue ? (
                                <button
                                  className="sp-btn -blue setting-tiktok-api__reconnect-btn"
                                  onClick={initiateTiktokConnect}
                                >
                                  {lang.__reconnect || "Reconnect"}
                                </button>
                              ) : null}
                              <button
                                className="sp-btn setting-tiktok-api__disconnect-btn setting-tiktok-api__disconnect-btn--secondary"
                                onClick={() =>
                                  disconnectTiktokAccount(accountId)
                                }
                                disabled={!accountId}
                              >
                                {lang.__disconnect || "Disconnect"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <WarningConnectModal
        show={showWarningConnectModal}
        setShow={setShowWarningConnectModal}
        setShowMetaDialog={() => {}}
        lang={lang}
        facebookPages={[]}
        instagramPages={[]}
        linePages={[]}
        tiktokPages={tiktokAccountsConnecting}
        savePage={saveTiktokWithSkipCheck}
      />
    </>
  );
}
